import React, { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { getUserDocuments } from 'src/redux/services/loggingServices';
import ViewIcon from 'src/assets/icons/icon-view.svg';
import DownloadIcon from 'src/assets/icons/icon-download.svg';
import SortByIcon from 'src/assets/icons/icon-sortby.svg';
import dropdownIcon from 'src/assets/icons/icon-dropdown-icon.svg';
import './UserDocuments.scss';
import NoData from '../../components/NoData';
import { Typography } from "src/latest/src/components";

interface Props {
  selectedUserId: string;
}
// Function to convert the date string to a JavaScript Date object

const parseDate = (dateStr: string) => {
  const [datePart, timePart] = dateStr.split(' ');
  const [month, day, year] = datePart.split('/').map(Number);
  const [hour, minute] = timePart.slice(0, -2).split(':').map(Number);
  const ampm = timePart.slice(-2);

  let hours = hour;

  if (ampm === 'PM' && hours < 12) hours += 12;
  if (ampm === 'AM' && hours === 12) hours = 0;

  // Adjust year to four digits if it's two digits
  const fullYear = year < 100 ? 2000 + year : year;

  return new Date(fullYear, month - 1, day, hours, minute);
};

const UserDocuments: React.FC<Props> = ({ selectedUserId }) => {
  const [userDocuments, setUserDocuments] = useState<{ fileItems: any[] }>({
    fileItems: [],
  });
  const [isDescending, setIsDescending] = useState(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchDocumentsData = async () => {
      try {
        const result = await getUserDocuments(selectedUserId);

        const sortedDocuments = result.fileItems.sort((a: any, b: any) => {
          const dateA = parseDate(a.added_at);
          const dateB = parseDate(b.added_at);

          return isDescending
            ? dateB.getTime() - dateA.getTime()
            : dateA.getTime() - dateB.getTime();
        });

        setUserDocuments({ fileItems: sortedDocuments });
        setIsLoading(false);
      } catch (error) {
        console.error('Failed to fetch documents', error);
      }
    };

    fetchDocumentsData();
  }, [selectedUserId, isDescending]);

  const handleViewClick = (url: string) => {
    window.open(url, '_blank');
  };

  const handleDownloadClick = (url: string, fileName: string) => {
    const anchor = document.createElement('a');

    if (url.startsWith('data:')) {
      anchor.href = url;
      anchor.download = fileName;
      anchor.click();
    } else {
      fetch(url)
        .then(response => {
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }

          return response.blob();
        })
        .then(blob => {
          const blobUrl = URL.createObjectURL(blob);

          anchor.href = blobUrl;
          anchor.download = fileName;
          anchor.click();
          URL.revokeObjectURL(blobUrl);
        })
        .catch(error => console.error('Failed to download file', error));
    }

    document.body.removeChild(anchor);
  };

  const handleSortClick = () => {
    setIsDescending(prev => !prev);
  };

  return (
    <div>
      {isLoading ? (
        <div className="th-parcticeprofile-loader">
          <Spinner animation="border" role="status" />
        </div>
      ) : (
        <>
          {userDocuments?.fileItems?.length === 0 ? (
            <div className="mt-3">
              <NoData message="No documents found" />
            </div>
          ) : (
            <div className="user-documents-container">
              <table>
                <thead>
                  <tr>
                    <th>
                      <Typography
                        className="fweight-600 color_doc"
                        variant="size-12"
                      >
                        FILE NAME
                      </Typography>
                    </th>
                    <th>
                      <Typography
                        className="fweight-600 color_doc"
                        variant="size-12"
                      >
                        DATE ADDED
                        <img
                          src={isDescending ? SortByIcon : dropdownIcon}
                          alt="SortByIcon"
                          className="sortby-icon ms-2"
                          onClick={handleSortClick}
                        />
                      </Typography>
                    </th>
                    <th />
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {userDocuments?.fileItems?.map((doc: any, index: number) => (
                    <tr key={index}>
                      <td>
                        <Typography
                          className="color_doc fweight"
                          variant="size-14"
                        >
                          {doc.name}
                        </Typography>
                      </td>
                      <td>
                        <Typography variant="size-14">
                          {doc.added_at}
                        </Typography>
                      </td>
                      <td className="action-cloumn">
                        <button
                          className="btn btn-trasparent"
                          onClick={() => handleViewClick(doc.url)}
                        >
                          <div className="d-flex">
                            <div className="me-3">
                              <img
                                src={ViewIcon}
                                alt="ViewIcon"
                                className="view-icon"
                              />
                            </div>
                            <div>
                              <Typography
                                className="color_doc fweight"
                                variant="size-14"
                              >
                                VIEW
                              </Typography>
                            </div>
                          </div>
                        </button>
                      </td>
                      <td className="action-cloumn">
                        <button
                          className="btn btn-trasparent"
                          onClick={() => handleDownloadClick(doc.url, doc.name)}
                        >
                          <div className="d-flex">
                            <div className="me-3">
                              <img
                                src={DownloadIcon}
                                alt="DownloadIcon"
                                className="download-icon"
                              />
                            </div>
                            <div>
                              <Typography
                                className="color-doc fweight"
                                variant="size-14"
                              >
                                DOWNLOAD
                              </Typography>
                            </div>
                          </div>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UserDocuments;
